import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Component } from "react";
import "@/App.css";
import Seo from "@/components/Seo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Portfolio from "@/pages/Portfolio";
import Builders from "@/pages/Builders";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    render() {
        if (this.state.error) {
            return (
                <div className="flex min-h-screen items-center justify-center bg-[#0B0F17] p-8 text-center">
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-100">Something went wrong.</h1>
                        <p className="mt-3 text-sm text-slate-400">Please refresh the page.</p>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);
    return null;
}

const META = {
    "/": {
        title: "Professional Painters Toronto & GTA | B&B Painting",
        description: "Interior, exterior and commercial painting across the Greater Toronto Area. Free quotes, real project photos, 5.0 Google rating. Call 647 510 4012.",
        path: "/",
    },
    "/services": {
        title: "Painting Services — Interior, Exterior & Commercial | B&B Painting",
        description: "Interior repainting, exterior painting, new construction and commercial painting across the GTA. Careful prep, clean lines, reliable crews, free quotes.",
        path: "/services",
    },
    "/portfolio": {
        title: "Our Work — Real Painting Projects from the GTA | B&B Painting",
        description: "Browse real completed projects — full home repaints, exteriors and renovations in Caledon, Mississauga, Orangeville and across the Greater Toronto Area.",
        path: "/portfolio",
    },
    "/builders": {
        title: "Painting Subcontractor for Builders & GCs | B&B Painting",
        description: "Dependable painting subcontractor for builders and general contractors in the GTA. Scheduled crews, clean job sites, snag-free finishes — book a walkthrough.",
        path: "/builders",
    },
    "/about": {
        title: "About B&B Painting and Renovation — 10+ Years, Ontario",
        description: "B&B Painting and Renovation — 10+ years of professional painting across Ontario. Built on quality, driven by trust. Meet the team behind the brush.",
        path: "/about",
    },
    "/contact": {
        title: "Contact — Free Painting Quote | B&B Painting and Renovation",
        description: "Tell us about your project. Call 647 510 4012 or send a quote request with photos — we respond with a clear, detailed estimate for your painting project.",
        path: "/contact",
    },
    "/admin": {
        title: "Owner Login — B&B Painting and Renovation",
        description: "Owner login for the B&B Painting and Renovation lead dashboard.",
        path: "/admin",
        noindex: true,
    },
};

function RouteMeta() {
    const { pathname } = useLocation();
    const meta = META[pathname] || META["/"];
    return <Seo {...meta} />;
}

function Shell() {
    const location = useLocation();

    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [location.pathname]);

    return (
        <>
            <ScrollToTop />
            <RouteMeta />
            <Navbar />
            <main>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/portfolio" element={<Portfolio />} />
                    <Route path="/builders" element={<Builders />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="*" element={<Home />} />
                </Routes>
            </main>
            <Footer />
        </>
    );
}

export default function App() {
    return (
        <ErrorBoundary>
            <BrowserRouter>
                <Shell />
                <Toaster theme="dark" position="top-right" richColors />
                <div className="noise-overlay" aria-hidden="true" />
            </BrowserRouter>
        </ErrorBoundary>
    );
}